import React, { useState } from "react";
import { toast } from "sonner";
import { X, ImagePlus, Video, CheckCircle2, AlertCircle } from "lucide-react";
import {
  presignSubmissionUpload,
  putToR2,
  finalizeSubmission,
} from "../lib/api";
import { processImage } from "../lib/imageProcessing";
import { inspectVideo } from "../lib/videoValidation";

/**
 * Upload flow for a card showcase.
 *
 * Steps:
 *   1. User picks image and/or video (both optional, at least one required).
 *   2. Client processes the image (HEIC→JPEG, downscale ≤4096, thumbnail,
 *      strip EXIF via canvas re-encode) and inspects the video for
 *      length/dimensions before uploading.
 *   3. Two/four presigned PUTs (image + thumbnail + video), one after
 *      the other so we can surface individual progress.
 *   4. POST /api/v1/submissions/finalize with the R2 keys; server
 *      re-verifies size + magic bytes and enqueues transcode.
 *   5. Success state — "pending review". Owner sees processing chip
 *      while video transcode is running.
 */
export default function UploadModal({ open, onClose, card, variant, existing, onSubmitted }) {
  const [imageFile, setImageFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [videoMeta, setVideoMeta] = useState(null);
  const [phase, setPhase] = useState("idle");      // idle | processing | uploading | finalizing | done | error
  const [progress, setProgress] = useState({});    // { image, thumb, video } 0..1
  const [errorMsg, setErrorMsg] = useState(null);

  const isEdit = !!existing;
  const submitLabel = isEdit ? "Replace media" : "Submit for review";
  const variantSlug = variant?.slug || variant?.variant_type || "normal";
  const variantLabel = variant?.variant_label || null;

  function reset() {
    setImageFile(null);
    setVideoFile(null);
    setVideoMeta(null);
    setPhase("idle");
    setProgress({});
    setErrorMsg(null);
  }

  function handleClose() {
    if (phase === "uploading" || phase === "processing" || phase === "finalizing") return;
    reset();
    onClose();
  }

  async function pickVideo(f) {
    setErrorMsg(null);
    try {
      const meta = await inspectVideo(f);
      setVideoFile(f);
      setVideoMeta(meta);
    } catch (e) {
      setVideoFile(null);
      setVideoMeta(null);
      setErrorMsg(e.message);
    }
  }

  async function submit() {
    if (!imageFile && !videoFile) {
      setErrorMsg("Add an image or a video (at least one).");
      return;
    }
    setErrorMsg(null);
    setPhase("processing");

    try {
      let imagePayload = null;
      let videoPayload = null;

      if (imageFile) {
        const { full, thumb } = await processImage(imageFile);
        setPhase("uploading");
        // Presign + PUT full
        const fullPresign = await presignSubmissionUpload({
          card_external_id: card.external_id,
          variant_slug: variantSlug,
          kind: "image",
          content_type: "image/jpeg",
          size: full.blob.size,
        });
        await putToR2(fullPresign.put_url, full.blob, "image/jpeg", (p) =>
          setProgress((prev) => ({ ...prev, image: p })),
        );
        const thumbPresign = await presignSubmissionUpload({
          card_external_id: card.external_id,
          variant_slug: variantSlug,
          kind: "thumbnail",
          content_type: "image/jpeg",
          size: thumb.blob.size,
        });
        await putToR2(thumbPresign.put_url, thumb.blob, "image/jpeg", (p) =>
          setProgress((prev) => ({ ...prev, thumb: p })),
        );
        imagePayload = {
          full: {
            key: fullPresign.key,
            content_type: "image/jpeg",
            width: full.width,
            height: full.height,
          },
          thumbnail: {
            key: thumbPresign.key,
            content_type: "image/jpeg",
            width: thumb.width,
            height: thumb.height,
          },
        };
      }

      if (videoFile) {
        setPhase("uploading");
        const presign = await presignSubmissionUpload({
          card_external_id: card.external_id,
          variant_slug: variantSlug,
          kind: "video",
          content_type: videoFile.type,
          size: videoFile.size,
        });
        await putToR2(presign.put_url, videoFile, videoFile.type, (p) =>
          setProgress((prev) => ({ ...prev, video: p })),
        );
        videoPayload = {
          key: presign.key,
          content_type: videoFile.type,
          duration_ms: videoMeta.duration_ms,
          width: videoMeta.width,
          height: videoMeta.height,
        };
      }

      setPhase("finalizing");
      const sub = await finalizeSubmission({
        card_external_id: card.external_id,
        variant_slug: variantSlug,
        image: imagePayload || undefined,
        video: videoPayload || undefined,
      });

      setPhase("done");
      toast.success(isEdit ? "Media replaced — pending review." : "Submitted for review.");
      onSubmitted?.(sub);
    } catch (e) {
      setPhase("error");
      const msg = e?.response?.data?.detail || e?.message || "Upload failed";
      setErrorMsg(msg);
      toast.error(msg);
    }
  }

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-bg/85 px-5 py-8"
      data-testid="upload-modal"
      onClick={handleClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="animate-rise w-full max-w-[560px] rounded-card border border-ink-chip bg-ink-surface shadow-[0_24px_60px_-20px_rgba(0,0,0,0.7)]"
      >
        <div className="flex items-center justify-between border-b border-ink-chip px-5 py-4">
          <div>
            <div className="text-eyebrow uppercase text-ink-muted">
              {isEdit ? "Replace media" : "Add your showcase"}
            </div>
            <div className="mt-0.5 text-title-section text-ink-text">
              {card.name}
              {variantLabel && (
                <span className="ml-2 text-body text-ink-muted"> · {variantLabel}</span>
              )}
            </div>
          </div>
          <button
            data-testid="upload-close"
            onClick={handleClose}
            className="rounded-chip p-1 text-ink-muted hover:bg-ink-raised hover:text-ink-text"
            aria-label="Close"
            disabled={phase === "uploading" || phase === "processing" || phase === "finalizing"}
          >
            <X size={18} />
          </button>
        </div>

        {phase === "done" ? (
          <div className="px-5 py-8 text-center" data-testid="upload-success">
            <CheckCircle2 size={32} className="mx-auto text-indigo-text" />
            <div className="mt-3 text-title-section text-ink-text">
              Pending review
            </div>
            <p className="mx-auto mt-2 max-w-[46ch] text-body text-ink-muted">
              Your showcase for <span className="text-ink-text">{card.name}</span> has
              been submitted. An admin will review it soon.
              {videoFile && " Video transcode may take a few seconds — refresh your profile to see it ready."}
            </p>
            <button
              data-testid="upload-done"
              onClick={handleClose}
              className="mt-6 inline-flex h-10 items-center rounded-card bg-[#4353E8] px-4 text-body font-semibold text-white"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="px-5 py-5">
            {existing && (
              <div className="mb-4 rounded-chip border border-indigo-chip-border bg-indigo-chip-bg px-3 py-2 text-meta text-indigo-chip-text">
                Replacing media returns your entry to pending. Your votes stay.
              </div>
            )}

            <FilePicker
              label="Photo"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              capture="environment"
              icon={ImagePlus}
              file={imageFile}
              onFile={setImageFile}
              disabled={phase !== "idle" && phase !== "error"}
              helper="JPEG, PNG, WEBP or HEIC · max 25 MB · we downscale + strip EXIF"
            />

            <div className="my-4 text-center text-meta text-ink-muted">and / or</div>

            <FilePicker
              label="Video"
              accept="video/mp4,video/quicktime,video/webm"
              icon={Video}
              file={videoFile}
              onFile={pickVideo}
              disabled={phase !== "idle" && phase !== "error"}
              helper="MP4, MOV or WEBM · ≤ 10 s · ≤ 100 MB · long edge ≤ 3840"
            />
            {videoMeta && (
              <div className="mt-2 text-meta text-ink-muted tabular">
                {videoMeta.width}×{videoMeta.height} · {(videoMeta.duration_ms / 1000).toFixed(1)}s ·{" "}
                {(videoMeta.size / 1024 / 1024).toFixed(1)} MB
              </div>
            )}

            {errorMsg && (
              <div
                data-testid="upload-error"
                className="mt-4 flex items-start gap-2 rounded-chip border border-[#4a1e1e] bg-[#2a1414] px-3 py-2 text-meta text-[#F4A6A6]"
              >
                <AlertCircle size={16} className="mt-[2px]" />
                <span>{errorMsg}</span>
              </div>
            )}

            {phase !== "idle" && phase !== "error" && (
              <div className="mt-4 space-y-2" data-testid="upload-progress">
                <ProgressLine label="Preparing" done={phase !== "processing"} />
                {imageFile && (
                  <>
                    <ProgressLine label="Uploading photo" value={progress.image} />
                    <ProgressLine label="Uploading thumbnail" value={progress.thumb} />
                  </>
                )}
                {videoFile && (
                  <ProgressLine label="Uploading video" value={progress.video} />
                )}
                <ProgressLine label="Finalizing" done={phase === "done"} />
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                data-testid="upload-cancel"
                onClick={handleClose}
                disabled={phase === "uploading" || phase === "processing" || phase === "finalizing"}
                className="rounded-card border border-ink-chip bg-transparent px-4 py-2 text-body text-ink-muted hover:text-ink-text disabled:cursor-not-allowed disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                data-testid="upload-submit"
                onClick={submit}
                disabled={phase === "processing" || phase === "uploading" || phase === "finalizing"}
                className="rounded-card bg-[#4353E8] px-4 py-2 text-body font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {phase === "processing" && "Processing…"}
                {phase === "uploading" && "Uploading…"}
                {phase === "finalizing" && "Finalizing…"}
                {(phase === "idle" || phase === "error") && submitLabel}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FilePicker({ label, accept, capture, icon: Icon, file, onFile, disabled, helper }) {
  const testid = `upload-${label.toLowerCase()}-input`;
  return (
    <div>
      <label className="mb-1 block text-eyebrow uppercase text-ink-muted">
        {label}
      </label>
      <label
        className={`flex cursor-pointer items-center gap-3 rounded-card border border-ink-chip bg-ink-raised px-4 py-3 transition-colors duration-cc ease-cc-ease hover:border-ink-muted ${
          disabled ? "opacity-60 pointer-events-none" : ""
        }`}
      >
        <Icon size={18} className="text-ink-muted" />
        <span className="flex-1 text-body text-ink-text truncate">
          {file ? file.name : `Choose a ${label.toLowerCase()}`}
        </span>
        <input
          data-testid={testid}
          type="file"
          accept={accept}
          capture={capture}
          className="hidden"
          onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
          disabled={disabled}
        />
      </label>
      {helper && (
        <div className="mt-1 text-meta text-ink-muted">{helper}</div>
      )}
    </div>
  );
}

function ProgressLine({ label, value, done }) {
  const pct = done ? 100 : Math.round((value || 0) * 100);
  const idle = !done && (value === undefined || value === 0);
  return (
    <div>
      <div className="flex items-center justify-between text-meta text-ink-muted tabular">
        <span>{label}</span>
        <span>{idle ? "…" : done ? "done" : `${pct}%`}</span>
      </div>
      <div className="mt-1 h-1 overflow-hidden rounded-full bg-ink-raised">
        <div
          className="h-full bg-indigo-text transition-all duration-200"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
